import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { getBranchProductsById } from '../../../../api/point';
import { errorMessages, formatterCurrency } from '../../../../utils/methods';
import ProductEditModal from '../modal/ProductEditModal';
import LockerEditModal from '../modal/LockerEditModal';
import useUserAuth from '../../../../utils/useUserAuth';

var qs = require('qs');

const initialQuery = {
  page: 1,
  limit: 10,
};

// Styled Components
const Container = styled(Box)({
  width: '100%',
});

const HeaderContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const SearchContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: '240px',
  height: '40px',
  border: '1px solid #e0e0e0',
  borderRadius: '4px',
  backgroundColor: '#fff',
  padding: '0 12px',
  '&:focus-within': {
    borderColor: '#4AB3BC',
  },
});

const ButtonContainer = styled(Box)({
  display: 'flex',
  gap: '10px',
});

const SearchInput = styled('input')({
  flex: 1,
  border: 'none',
  outline: 'none',
  fontSize: '16px',
  color: '#333',
  '&::placeholder': {
    color: '#999',
  },
});

const SearchIcon = styled(Box)({
  width: '20px',
  height: '20px',
  color: '#999',
  cursor: 'pointer',
});

const LockerAddButton = styled(Button)({
  height: '40px',
  padding: '0 20px',
  borderRadius: '4px',
  border: '1px solid #4AB3BC',
  background: '#fff',
  fontSize: '16px',
  color: '#4AB3BC',
  '&:hover': {
    background: '#fff',
  },
});

const AddButton = styled(Button)({
  height: '40px',
  padding: '0 20px',
  borderRadius: '4px',
  background: '#4AB3BC',
  fontSize: '16px',
  color: '#FFFFFF',
  '&:hover': {
    background: '#4AB3BC',
  },
});

const StyledTableContainer = styled(TableContainer)({
  borderRadius: '4px',
  marginTop: '16px',
});

const StyledTableHead = styled(TableHead)({
  background: '#f2f2f2',
  borderRadius: '20px 20px 0 0',
});

const HeaderCell = styled(TableCell)({
  fontWeight: 500,
  fontSize: '13px',
  color: '#666',
});

const StyledTableRow = styled(TableRow)({
  height: '52px',
  '&:hover': {
    backgroundColor: '#fafafa',
  },
});

const BodyCell = styled(TableCell)({
  fontSize: '14px',
  color: '#333',
});

const ProductTypeBadge = styled(Box, {
  shouldForwardProp: prop => prop !== 'productType',
})(({ productType }) => ({
  display: 'inline-block',
  padding: '4px 12px',
  backgroundColor: productType === 'PERIOD' ? '#FFB659' : '#55C982',
  color: '#ffffff',
  borderRadius: '4px',
  fontSize: '16px',
  fontWeight: 500,
  whiteSpace: 'nowrap',
}));

const EditButton = styled(Button)({
  width: 'auto',
  height: '32px',
  padding: '0 16px',
  border: '1px solid #d9d9d9',
  borderRadius: '4px',
  color: '#55C982',
  fontSize: '16px',
  fontWeight: 400,
});

const PaginationContainer = styled(Stack)({
  marginTop: '20px',
  marginLeft: '40%',
});

const StyledPagination = styled(Pagination)({
  '& .MuiPaginationItem-root.MuiPaginationItem-sizeMedium': {
    background: '#e1e1e1',
    border: 'none',
    height: '24px',
    minWidth: '24px',
    marginBottom: '150px',
  },
  '& .MuiPaginationItem-root.MuiPaginationItem-sizeMedium.Mui-selected': {
    background: '#0F7F85',
    color: '#fff',
  },
  '.css-g2z002-MuiSvgIcon-root-MuiPaginationItem-icon': {
    color: '#fff',
  },
});

// ============================================
// Main Component
// ============================================
export default function ProductSide({ branchId, user, categories, selectedCategoryId }) {
  const { t } = useTranslation();
  const toast = useToast();
  const canProductEdit = useUserAuth('CENTER_PRODUCT_EDIT');
  const [query, setQuery] = useState(initialQuery);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeProduct, setActiveProduct] = useState({});
  const [lockerModalOpen, setLockerModalOpen] = useState(false);
  const [lockerEditData, setLockerEditData] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');

  // 카테고리 변경 시 페이지를 1로 리셋
  useEffect(() => {
    setQuery(prev => ({ ...prev, page: 1 }));
  }, [selectedCategoryId]);

  // 락커 카테고리 여부
  const isLockerCategory = selectedCategoryId === '락커';

  // React Query로 상품 목록 조회 (전체 조회 후 프론트에서 페이지네이션)
  const { data: products } = useQuery({
    queryKey: ['products', branchId, selectedCategoryId],
    queryFn: async () => {
      const queryParams = { page: 1, limit: 1000000 };
      // 락커가 아닌 경우 카테고리 ID 전달
      if (selectedCategoryId && selectedCategoryId !== '락커') {
        queryParams.categoryId = selectedCategoryId;
      }
      const queryString = qs.stringify(queryParams);
      const data = await getBranchProductsById(user.token, branchId, queryString);
      return data;
    },
    enabled: !!branchId && !!user?.token,
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  // 필터링된 전체 상품 목록
  const allFilteredProducts =
    products?.content?.filter(product => {
      // 카테고리 필터
      let categoryMatch = true;
      if (selectedCategoryId !== null) {
        if (isLockerCategory) {
          categoryMatch = product.productCategoryType === 'LOCKER';
        } else {
          categoryMatch = product.categoryId === selectedCategoryId;
        }
      }

      // 검색어 필터
      const searchMatch = searchKeyword
        ? product.productName?.toLowerCase().includes(searchKeyword.toLowerCase())
        : true;

      return categoryMatch && searchMatch;
    }) || [];

  // 프론트엔드 페이지네이션 적용
  const filteredProducts = allFilteredProducts.slice((query.page - 1) * query.limit, query.page * query.limit);

  // 총 개수 (필터링된 전체 상품 기준)
  const totalElements = allFilteredProducts.length;

  const handleChangePage = (event, value) => {
    setQuery({ ...query, page: value });
  };

  const handleOpenModal = (product = {}) => {
    setActiveProduct(product);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setActiveProduct({});
  };

  // 라커 상품은 전용 라커 모달로, 그 외 상품은 일반 상품 모달로 수정
  const handleEditProduct = product => {
    if (product?.productCategoryType === 'LOCKER') {
      setLockerEditData(product);
      setLockerModalOpen(true);
    } else {
      handleOpenModal(product);
    }
  };

  // 라커 상품 추가 (신규 등록)
  const handleOpenLockerEditModal = () => {
    setLockerEditData(null);
    setLockerModalOpen(true);
  };

  const handleLockerCloseModal = () => {
    setLockerModalOpen(false);
    setLockerEditData(null);
  };

  return (
    <Container>
      <HeaderContainer>
        {/* 검색 필드 */}
        <SearchContainer>
          <SearchInput
            placeholder={t('상품명을 입력해 주세요')}
            value={searchKeyword}
            onChange={e => setSearchKeyword(e.target.value)}
          />
          <SearchIcon component="svg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path d="M20 20L16.5 16.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </SearchIcon>
        </SearchContainer>

        {canProductEdit && (
          <ButtonContainer>
            <LockerAddButton onClick={handleOpenLockerEditModal}>+ {t('라커 상품 추가')}</LockerAddButton>
            <AddButton onClick={() => handleOpenModal()}>+ {t('일반 상품 추가')}</AddButton>
          </ButtonContainer>
        )}
      </HeaderContainer>

      {filteredProducts?.length ? (
        <>
          <StyledTableContainer>
            <Table size="small">
              <StyledTableHead>
                <TableRow sx={{ height: '44px' }}>
                  <HeaderCell align="center" sx={{ width: '100px', fontSize: '16px' }}>
                    {t('상품 유형')}
                  </HeaderCell>
                  <HeaderCell align="center" sx={{ width: '100px', fontSize: '16px' }}>
                    {t('분류')}
                  </HeaderCell>
                  <HeaderCell align="center" sx={{ fontSize: '16px' }}>
                    {t('상품명')}
                  </HeaderCell>
                  <HeaderCell align="center" sx={{ width: '100px', fontSize: '16px' }}>
                    {t('가격')}
                  </HeaderCell>
                  <HeaderCell align="center" sx={{ width: '100px', fontSize: '16px' }}>
                    {t('재고&기간')}
                  </HeaderCell>
                  {/* <HeaderCell align="center" sx={{ width: '120px', fontSize: '16px' }}>
                    {t('최근 수정일')}
                  </HeaderCell> */}
                  <HeaderCell align="center" sx={{ width: '80px', fontSize: '16px' }}>
                    {t('기타')}
                  </HeaderCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {filteredProducts?.map((product, index) => (
                  <StyledTableRow key={index}>
                    {/* 상품 유형 */}
                    <BodyCell align="center">
                      <ProductTypeBadge productType={product.productType}>
                        {product.productType === 'PERIOD' ? t('기간 상품') : t('재고 상품')}
                      </ProductTypeBadge>
                    </BodyCell>
                    {/* 분류 */}
                    <BodyCell align="center" sx={{ fontSize: '16px' }}>
                      {product.categoryName}
                    </BodyCell>
                    {/* 상품명 */}
                    <BodyCell align="center" sx={{ fontSize: '16px' }}>
                      {product.productName}
                    </BodyCell>
                    {/* 가격 */}
                    <BodyCell align="center" sx={{ fontSize: '16px' }}>
                      {formatterCurrency(product.price)}원
                    </BodyCell>
                    {/* 재고&기간 */}
                    <BodyCell align="center" sx={{ fontSize: '16px' }}>
                      {product.productType === 'PERIOD'
                        ? `${product.periodValue} ${product.periodType === 'MONTH' ? '개월' : '일'}`
                        : `${formatterCurrency(product.stockQuantity)} 개`}
                    </BodyCell>
                    {/* 최근 수정일 */}
                    {/* <BodyCell align="center" sx={{ fontSize: '16px' }}>
                      {product.updatedAt ? new Date(product.updatedAt).toLocaleDateString('ko-KR') : '-'}
                    </BodyCell> */}
                    {/* 기타 (수정 버튼) */}
                    {canProductEdit && (
                      <BodyCell align="center" sx={{ fontSize: '16px' }}>
                        <EditButton onClick={() => handleEditProduct(product)}>{t('수정')}</EditButton>
                      </BodyCell>
                    )}
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>

          {totalElements > query.limit ? (
            <PaginationContainer spacing={1}>
              <StyledPagination
                count={Math.ceil(totalElements / query.limit)}
                onChange={handleChangePage}
                page={query.page}
                color="primary"
                variant="outlined"
                shape="rounded"
              />
            </PaginationContainer>
          ) : (
            <Box sx={{ height: '40px' }} />
          )}
        </>
      ) : null}

      {/* 상품 등록/수정 모달 */}
      <ProductEditModal
        open={modalOpen}
        onClose={handleCloseModal}
        branchId={branchId}
        user={user}
        categories={categories}
        activeProduct={activeProduct}
      />

      {/* 락커 등록/수정 모달 */}
      <LockerEditModal
        open={lockerModalOpen}
        onClose={handleLockerCloseModal}
        branchId={branchId}
        user={user}
        editData={lockerEditData}
        isEditMode={!!lockerEditData}
      />
    </Container>
  );
}
